'use client';

import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { apiClient } from '@/app/api/client';
import { ClipboardDocumentIcon, TrashIcon, PlusIcon, KeyIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface ApiKey {
    id: string;
    name: string;
    key: string;
    keyPreview: string;
    permissions: string[];
    isActive: boolean;
    createdAt: number;
    lastUsedAt: number | null;
    usageCount: number;
    rateLimit: number;
}

export default function ApiKeysManager() {
    const [keys, setKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['read']);
    const [creating, setCreating] = useState(false);
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [regenerateKeyId, setRegenerateKeyId] = useState<string | null>(null);

    // Confirm dialog state
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        confirmText?: string;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
    });

    // Fetch API keys
    const fetchKeys = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/api/users/api-keys');
            setKeys(response.data.keys || []);
        } catch (error) {
            console.error('Failed to fetch API keys:', error);
            toast.error('Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKeys();
    }, []);

    // Create new API key
    const handleCreate = async () => {
        if (!newKeyName.trim()) {
            toast.error('Please enter a name for the API key');
            return;
        }

        try {
            setCreating(true);
            const response = await apiClient.post('/api/users/api-keys', {
                name: newKeyName.trim(),
                permissions: newKeyPermissions,
                rateLimit: 1000
            });

            setCreatedKey(response.data.key);
            toast.success('API key created successfully!');
            fetchKeys();
        } catch (error: any) {
            console.error('Failed to create API key:', error);
            toast.error(error.response?.data?.error || 'Failed to create API key');
        } finally {
            setCreating(false);
        }
    };

    // Regenerate API key (revoke old, create new with same name)
    const handleRegenerate = (id: string, name: string, permissions: string[]) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Regenerate API Key',
            message: `Regenerate API key "${name}"? The old key will stop working immediately and a new key will be generated.`,
            confirmText: 'Regenerate',
            onConfirm: async () => {
                try {
                    setRegenerating(true);
                    setRegenerateKeyId(id);

                    // Regenerate the key (backend should handle updating the same entry)
                    const response = await apiClient.put(`/api/users/api-keys/${id}/regenerate`, {
                        permissions: permissions,
                        rateLimit: 1000
                    });

                    setCreatedKey(response.data.key);
                    setShowCreateModal(true); // Show the modal with the new key
                    toast.success('API key regenerated successfully!');
                    fetchKeys();
                } catch (error: any) {
                    console.error('Failed to regenerate API key:', error);
                    toast.error(error.response?.data?.error || 'Failed to regenerate API key');
                } finally {
                    setRegenerating(false);
                    setRegenerateKeyId(null);
                }
            },
        });
    };

    // Revoke API key (delete permanently)
    const handleRevoke = (id: string, name: string) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Delete API Key',
            message: `Are you sure you want to permanently delete the API key "${name}"? This action cannot be undone and the key will stop working immediately.`,
            confirmText: 'Delete',
            onConfirm: async () => {
                try {
                    await apiClient.delete(`/api/users/api-keys/${id}`);
                    toast.success('API key deleted successfully');
                    fetchKeys();
                } catch (error) {
                    console.error('Failed to delete API key:', error);
                    toast.error('Failed to delete API key');
                }
            },
        });
    };

    // Copy to clipboard (with fallback for non-HTTPS)
    const copyToClipboard = async (text: string) => {
        try {
            // Try modern clipboard API first (requires HTTPS)
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                toast.success('Copied to clipboard!');
            } else {
                // Fallback for non-HTTPS (localhost)
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();

                try {
                    const successful = document.execCommand('copy');
                    if (successful) {
                        toast.success('Copied to clipboard!');
                    } else {
                        throw new Error('Copy command failed');
                    }
                } catch (err) {
                    console.error('Fallback copy failed:', err);
                    toast.error('Failed to copy. Please copy manually.');
                } finally {
                    document.body.removeChild(textArea);
                }
            }
        } catch (err) {
            console.error('Failed to copy:', err);
            toast.error('Failed to copy. Please copy manually.');
        }
    };

    // Close create modal
    const closeCreateModal = () => {
        setShowCreateModal(false);
        setNewKeyName('');
        setNewKeyPermissions(['read']);
        setCreatedKey(null);
    };

    // Format date
    const formatDate = (timestamp: number | null) => {
        if (!timestamp) return 'Never';
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                        API Keys
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage API keys for your Content Growth widget and integrations
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Key
                </button>
            </div>

            <div className="border-t border-gray-200">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading API keys...</p>
                    </div>
                ) : keys.length === 0 ? (
                    <div className="p-12 text-center">
                        <KeyIcon className="h-12 w-12 text-gray-400 mx-auto" />
                        <h3 className="mt-4 text-lg font-medium text-gray-900">No API keys</h3>
                        <p className="mt-2 text-sm text-gray-600">
                            Create your first API key to start using the Content Growth widget
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {keys.map((key) => (
                            <li key={key.id} className="px-6 py-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3">
                                            <KeyIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                            <div>
                                                <h3 className="text-lg font-medium text-gray-900">{key.name}</h3>
                                                <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                                                    <code className="px-2 py-1 bg-gray-100 rounded font-mono text-xs">
                                                        {key.keyPreview}
                                                    </code>
                                                    <span className="text-xs text-amber-600 font-medium">
                                                        ⚠️ Full key only shown once at creation
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex items-center gap-6 text-sm text-gray-500">
                                            <span>
                                                Permissions: <span className="font-medium text-gray-700">{key.permissions.join(', ')}</span>
                                            </span>
                                            <span>
                                                Created: <span className="font-medium text-gray-700">{formatDate(key.createdAt)}</span>
                                            </span>
                                            <span>
                                                Last used: <span className="font-medium text-gray-700">{formatDate(key.lastUsedAt)}</span>
                                            </span>
                                            <span>
                                                Usage: <span className="font-medium text-gray-700">{key.usageCount.toLocaleString()} requests</span>
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ml-4 flex-shrink-0 flex gap-2">
                                        <button
                                            onClick={() => handleRegenerate(key.id, key.name, key.permissions)}
                                            disabled={regenerating && regenerateKeyId === key.id}
                                            className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                        >
                                            <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            {regenerating && regenerateKeyId === key.id ? 'Regenerating...' : 'Regenerate'}
                                        </button>
                                        <button
                                            onClick={() => handleRevoke(key.id, key.name)}
                                            className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                        >
                                            <TrashIcon className="h-4 w-4 mr-1" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Create Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        {createdKey ? (
                            /* Show created key */
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    🎉 {regenerateKeyId ? 'API Key Regenerated!' : 'API Key Created!'}
                                </h3>
                                <div className="bg-red-50 border-2 border-red-300 rounded-md p-4 mb-4">
                                    <p className="text-base text-red-800 font-bold mb-3">
                                        🚨 IMPORTANT: Copy this key NOW!
                                    </p>
                                    <p className="text-sm text-red-700 mb-3">
                                        This is the ONLY time you'll see the full key. After closing this window, only a preview will be available.
                                    </p>
                                    <code className="block p-3 bg-white border-2 border-red-400 rounded text-sm font-mono break-all select-all">
                                        {createdKey}
                                    </code>
                                    <button
                                        onClick={() => copyToClipboard(createdKey)}
                                        className="mt-3 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                                    >
                                        <ClipboardDocumentIcon className="h-5 w-5 mr-2" />
                                        📋 Copy Full Key to Clipboard
                                    </button>
                                </div>
                                <button
                                    onClick={closeCreateModal}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    I've copied the key, close this window
                                </button>
                            </div>
                        ) : (
                            /* Create form */
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Create API Key</h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={newKeyName}
                                            onChange={(e) => setNewKeyName(e.target.value)}
                                            placeholder="e.g., Production Widget"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Permissions
                                        </label>
                                        <div className="space-y-2">
                                            {['read', 'write', 'admin'].map((perm) => (
                                                <label key={perm} className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={newKeyPermissions.includes(perm)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setNewKeyPermissions([...newKeyPermissions, perm]);
                                                            } else {
                                                                setNewKeyPermissions(newKeyPermissions.filter(p => p !== perm));
                                                            }
                                                        }}
                                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-700 capitalize">{perm}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-6 flex gap-3">
                                    <button
                                        onClick={handleCreate}
                                        disabled={creating}
                                        className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        {creating ? 'Creating...' : 'Create'}
                                    </button>
                                    <button
                                        onClick={closeCreateModal}
                                        disabled={creating}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
            />
        </div>
    );
}
